"use client";

import type { ReactNode } from "react";
import { Loader2, PlusCircle } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import type {
  SupportTicketCategory,
  SupportTicketListItem,
  SupportTicketStatus,
  SupportTicketVisibility,
} from "@/lib/api";

export const SUPPORT_CARD_CLASS =
  "rounded-2xl border border-aera-lightgray/80 bg-aera-white";

export const CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  ISSUE: "ปัญหา / บั๊ก",
  REQUEST: "ขอฟีเจอร์",
  SUGGESTION: "ข้อเสนอแนะ",
};

export const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  OPEN: "เปิดอยู่",
  WAITING_USER: "รอคุณตอบ",
  IN_PROGRESS: "กำลังดำเนินการ",
  RESOLVED: "แก้ไขแล้ว",
  CLOSED: "ปิดแล้ว",
};

export function CategoryBadge({ category }: { category: SupportTicketCategory }) {
  const colors: Record<SupportTicketCategory, string> = {
    ISSUE: "bg-aera-redlight text-aera-red",
    REQUEST: "bg-aera-warmcream text-aera-amber",
    SUGGESTION: "bg-aera-deeppurple/10 text-aera-deeppurple",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[category]}`}
    >
      {CATEGORY_LABELS[category]}
    </span>
  );
}

export function StatusBadge({ status }: { status: SupportTicketStatus }) {
  const colors: Record<SupportTicketStatus, string> = {
    OPEN: "bg-aera-greenlight text-aera-green",
    WAITING_USER: "bg-aera-warmcream text-aera-amber",
    IN_PROGRESS: "bg-aera-deeppurple/10 text-aera-deeppurple",
    RESOLVED: "bg-aera-lightgray text-aera-charcoal",
    CLOSED: "bg-aera-redlight text-aera-red",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export function VisibilityBadge({ visibility }: { visibility: SupportTicketVisibility }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        visibility === "PRIVATE"
          ? "bg-aera-deeppurple/10 text-aera-deeppurple"
          : "bg-aera-warmcream text-aera-amber"
      }`}
    >
      {visibility === "PRIVATE" ? "ส่วนตัว" : "สาธารณะ"}
    </span>
  );
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

export function formatReplyCount(count: number): string {
  if (count === 0) return "ยังไม่มีตอบกลับ";
  return `${count.toLocaleString("th-TH")} ตอบกลับ`;
}

export function loadErrorToThai(message: string | null | undefined): string {
  if (!message?.trim()) return "โหลดรายการตั๋วไม่สำเร็จ";
  const raw = message.trim();
  if (/[\u0E00-\u0E7F]/.test(raw)) return raw;
  const m = raw.toLowerCase();
  if (/network|fetch failed|failed to fetch|timeout/.test(m)) {
    return "เชื่อมต่อไม่สำเร็จ กรุณาลองใหม่";
  }
  return "โหลดรายการตั๋วไม่สำเร็จ กรุณาลองใหม่";
}

export function SupportPageHeader({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`${SUPPORT_CARD_CLASS} p-4 md:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-warmcream">
            {icon}
          </span>
          <div>
            <p className="font-semibold text-aera-charcoal">{title}</p>
            <p className="text-xs leading-relaxed text-aera-gray">
              {description}
            </p>
          </div>
        </div>
        <AeraButton
          href="/support/new/issue"
          variant="primary"
          fontWeight="medium"
          icon={<PlusCircle className="h-4 w-4" aria-hidden />}
          className="shrink-0"
        >
          สร้างตั๋ว
        </AeraButton>
      </div>
      {children}
    </div>
  );
}

export function SupportLoadingState({
  label = "กำลังโหลด…",
}: {
  label?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-aera-gray">
      <Loader2 className="h-8 w-8 animate-spin text-aera-amber" aria-hidden />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function SupportErrorState({
  error,
  onRetry,
  className,
}: {
  error: string;
  onRetry: () => void;
  className?: string;
}) {
  return (
    <div className={`space-y-3 ${className ?? ""}`}>
      <p
        className="rounded-lg bg-aera-redlight px-3 py-2 text-sm text-aera-red"
        role="alert"
      >
        {error}
      </p>
      <AeraButton
        type="button"
        variant="outline"
        fontWeight="medium"
        onClick={onRetry}
      >
        ลองใหม่
      </AeraButton>
    </div>
  );
}

export function SupportEmptyState({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-aera-warmcream">
        {icon}
      </span>
      <p className="mt-4 text-sm font-semibold text-aera-charcoal">{title}</p>
      <p className="mt-1 max-w-xs text-xs text-aera-gray">{description}</p>
      <AeraButton
        href="/support/new/issue"
        variant="primary"
        fontWeight="medium"
        icon={<PlusCircle className="h-4 w-4" aria-hidden />}
        className="mt-4"
      >
        สร้างตั๋วแรก
      </AeraButton>
    </div>
  );
}

export function SupportTicketList({
  tickets,
  showVisibility = false,
  showAuthor = false,
}: {
  tickets: SupportTicketListItem[];
  showVisibility?: boolean;
  showAuthor?: boolean;
}) {
  return (
    <ul className="space-y-2">
      {tickets.map((t) => (
        <li key={t.publicId}>
          <AeraButton
            href={`/support/tickets/${t.publicId}`}
            variant="ghost"
            className="block cursor-pointer rounded-xl border border-aera-lightgray/80 bg-aera-offwhite p-4 transition hover:border-aera-amber/40 hover:bg-aera-warmcream/30 h-auto! w-full! items-start! text-left!"
          >
            <div className="w-full">
              <div className="flex flex-wrap items-center gap-2">
                <CategoryBadge category={t.category} />
                {showVisibility && (
                  <VisibilityBadge visibility={t.visibility} />
                )}
                <StatusBadge status={t.status} />
              </div>
              <h3 className="mt-2 line-clamp-2 font-semibold text-aera-charcoal">
                {t.title}
              </h3>
              <p className="mt-1.5 text-xs text-aera-gray">
                {showAuthor && (
                  <>{t.displayName?.trim() || "ไม่ระบุตัวตน"} · </>
                )}
                {formatReplyCount(t.replyCount)} · {formatWhen(t.lastActivityAt)}
              </p>
            </div>
          </AeraButton>
        </li>
      ))}
    </ul>
  );
}
